// Multi-action route authoring (P3b-6, gap #10). Two pure helpers built on the
// existing single-action route mapping in `route-effect.ts`. Route-only: relaxes
// the P2 "one step = one single-action rule" decision (D2) for the Route bucket.
//
// N=1 byte-parity is non-negotiable: compileMultiActionRoute(meta, [draft]) is
// byte-identical to makeRouteEffect(draft.effectKey).compile(equivalentStep). The
// per-effect registry compile/parse and the listing/identifyRuleStep (which keys
// off actions[0]) are unchanged.
import { isActionType } from '../../form-config/rule-attachment.js';
import { MOMENT, ruleTypeFor, momentForTrigger } from './route-effect.js';
/** Reverse of ruleTypeFor: the persisted action `type` → the effect key. Only
 *  'route' collapses (→ route_to_queue); every other type is its own effect key. */
function effectKeyForType(type) {
    return type === 'route' ? 'route_to_queue' : type;
}
/**
 * Compile rule-level meta + an ordered action chain into a CreateRuleAttachmentInput.
 * `type` = the FIRST action's rule type (mirrors migrateRuleConfig + the legacy editor);
 * trigger/scope/stage come from the shared MOMENT table; top-level config is `{}`.
 * Each action carries its own params on `config` — no top-level `action.message`, so a
 * single action is byte-identical to makeRouteEffect's output.
 * Callers guarantee ≥1 action (the drawer disables Save when empty).
 */
export function compileMultiActionRoute(meta, actions) {
    const m = MOMENT[meta.moment] ?? MOMENT.step_enter;
    const first = actions[0];
    const firstType = ruleTypeFor(first ? first.effectKey : 'route_to_queue');
    return {
        name: meta.name,
        type: firstType,
        scope: m.scope,
        trigger: m.trigger,
        stage: meta.stage ?? m.stage,
        severity: meta.severity,
        overrideBehavior: meta.overrideBehavior,
        overrideConfig: meta.overrideConfig,
        config: {},
        condition: meta.condition,
        actions: actions.map((a, i) => ({
            id: `${a.effectKey}-${i}`,
            type: ruleTypeFor(a.effectKey),
            config: a.config,
            displayOrder: i,
        })),
    };
}
/**
 * Reconstruct rule-level meta + ALL actions from a persisted route rule. Removes
 * the D2 first-action-only lossy read. Returns null when the payload has no actions
 * or its first action is not a workflow-action type (i.e. it's a check rule).
 */
export function parseMultiActionRoute(payload) {
    if (!payload || typeof payload !== 'object')
        return null;
    const rule = payload;
    const raw = rule.actions;
    if (!Array.isArray(raw) || raw.length === 0)
        return null;
    const firstType = raw[0].type;
    if (typeof firstType !== 'string' || !isActionType(firstType))
        return null;
    const actions = raw.map((a) => ({
        effectKey: effectKeyForType(String(a.type)),
        config: a.config ?? {},
    }));
    const meta = {
        name: typeof rule.name === 'string' ? rule.name : '',
        severity: rule.severity === 'warning' ? 'warning' : 'error',
        overrideBehavior: rule.overrideBehavior,
        overrideConfig: rule.overrideConfig,
        moment: momentForTrigger(String(rule.trigger)),
        stage: rule.stage,
        condition: rule.condition,
    };
    return { meta, actions };
}
//# sourceMappingURL=route-multi.js.map